import { BusinessObjectBase, ModelBase } from '@skylab-shared/core';
import { BankAccountCollectionQuery, BankAccountItem } from '../../bank-account/queries/bank-account.query-base';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../../currency/queries/currency.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';

interface ModelPart extends ModelBase {
    communityTransactionId: number;
    currencyId: number;
    bankAccountNumberId?: number | null;
    baseCurrencyBankAccountNumberId?: number | null;
    paymentMethodId: number;
}

export abstract class BankAccountNumberMixin<TModel extends ModelPart> extends BusinessObjectBase<TModel>  {

    public updateBankAccountNumber(): void {
        if (this.paymentMethodAssociatedWithBankAccount) {
            const defaultDocumentCurrencyBankAccountNumber = this.getDefaultDocumentCurrencyBankAccount();
            const defaultCommunityTransactionId = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { default: true }).id;

            this.model.bankAccountNumberId = defaultDocumentCurrencyBankAccountNumber
                ? defaultDocumentCurrencyBankAccountNumber.id
                : this.model.bankAccountNumberId;

            if (this.inForeginCurrency()) {
                if (!this.model.baseCurrencyBankAccountNumberId) {
                    const defaultBaseCurrencyBankAccount = this.getDefaultBaseCurrencyBankAccount();
                    if (defaultBaseCurrencyBankAccount) {
                        this.model.baseCurrencyBankAccountNumberId = defaultBaseCurrencyBankAccount.id !== this.model.bankAccountNumberId
                            ? defaultBaseCurrencyBankAccount.id
                            : null;
                    } else {
                        this.model.baseCurrencyBankAccountNumberId  = null;
                    }
                }
            }

            if ((this.model.communityTransactionId !== defaultCommunityTransactionId) || !this.inForeginCurrency()) {
                this.model.baseCurrencyBankAccountNumberId = null;
            }
        } else {
            this.model.bankAccountNumberId = null;

            if (this.inForeginCurrency()) {
                this.model.baseCurrencyBankAccountNumberId = null;
            }
        }
    }

    private get paymentMethodAssociatedWithBankAccount(): boolean {
        if (!this.model.paymentMethodId) {
            return false;
        }

        const { associatedWithBankAccount } = this.localContext.firstPreloaded(PaymentMethodQuery, { id: this.model.paymentMethodId });
        return associatedWithBankAccount;
    }

    private getDefaultDocumentCurrencyBankAccount(): BankAccountItem {
        return this.localContext.firstPreloaded(BankAccountCollectionQuery, { currencyId: this.model.currencyId, default: true });
    }

    private getDefaultBaseCurrencyBankAccount(): BankAccountItem {
        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        const { id } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
        return this.localContext.firstPreloaded(BankAccountCollectionQuery, { currencyId: id, default: true });
    }

    private inForeginCurrency(): boolean {
        if (!this.model.currencyId) return false;

        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        return this.model.currencyId !== this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;
    }
}
